/**
 * FloorPlan2DTldraw — A.1 SOTA vector-canvas scaffold.
 *
 * The existing renderFloorPlanSvg + FloorPlanOverlay stack ships
 * today and works. This is the next-gen substrate built on tldraw v5
 * — vector primitives, pan/zoom, selection, keyboard shortcuts — that
 * eventually replaces the SVG path when the designer-tooling features
 * (multi-select, snapping, alignment guides) demand it.
 *
 * Gated behind env.VITE_FLOOR_PLAN_TLDRAW so the existing path stays
 * default. Enable for one designer at a time as we iterate.
 *
 * Roadmap:
 *   1. [now] Read-only mirror of the SVG renderer — same room shell,
 *      same SKU rects, no drag yet. Proves the canvas substrate.
 *   2. Wire SKU rects to setPlacementPosition on drag-release.
 *   3. tldraw's built-in snap lines + alignment guides.
 *   4. Multi-select + bulk move / rotate.
 *   5. Replace the SVG renderer entirely once feature parity ships.
 */
import { useEffect, useMemo } from "react";
import { Tldraw, type Editor, createShapeId, AssetRecordType } from "tldraw";
import "tldraw/tldraw.css";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { type PlanMetrics } from "../../lib/floorplanSvg";
import codegen from "../../../../docs/ontology/codegen.json";

interface CategoryRow {
  category: string;
  wM: number;
  dM: number;
  anchor: string;
  color: string;
}
const CATEGORY_TABLE = new Map<string, CategoryRow>(
  (codegen.categoryFootprints as ReadonlyArray<CategoryRow>).map((r) => [r.category, r]),
);

function attrNum(n: GraphNode, k: string): number | null {
  const v = n.attrs[k];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function attrStr(n: GraphNode, k: string): string | null {
  const v = n.attrs[k];
  return typeof v === "string" ? v : null;
}

interface Props {
  readonly graph: ProjectGraph;
  readonly roomId: string;
  readonly metrics: PlanMetrics;
}

export function FloorPlan2DTldraw({ graph, roomId, metrics }: Props): JSX.Element {
  // Build a deterministic set of shapes (room shell + SKU rects) the
  // editor renders on mount. We don't persist tldraw's own document
  // yet — every render rebuilds from the graph so the source-of-truth
  // is always the typed graph, not tldraw's snapshot.
  const skus = useMemo(() => {
    const ids = new Set(graph.edges
      .filter((e) => e.kind === "IN_ROOM" && e.target === roomId)
      .map((e) => e.source));
    return graph.nodes.filter((n) => n.kind === "SKU" && ids.has(n.id));
  }, [graph, roomId]);

  function setup(editor: Editor): void {
    editor.updateInstanceState({ isReadonly: true });
    // Room shell — outer rectangle. tldraw coords are page-space px;
    // we reuse PlanMetrics so the canvas aligns with the existing SVG.
    const px = metrics.originPx;
    const roomW = metrics.roomFt.w * metrics.pxPerFt;
    const roomD = metrics.roomFt.d * metrics.pxPerFt;
    editor.createShape({
      id: createShapeId(`room-${roomId}`),
      type: "geo",
      x: px.x,
      y: px.y,
      props: {
        geo: "rectangle",
        w: roomW,
        h: roomD,
        color: "grey",
        fill: "none",
        size: "m",
      },
    });
    // SKU rectangles — read posX_${roomId} / posZ_${roomId} for the
    // user-dragged override, fall back to the category default.
    for (const sku of skus) {
      const cat = attrStr(sku, "category") ?? "Item";
      const row = CATEGORY_TABLE.get(cat) ?? CATEGORY_TABLE.get("Item");
      if (!row) continue;
      const wPx = row.wM * metrics.ftPerM * metrics.pxPerFt;
      const dPx = row.dM * metrics.ftPerM * metrics.pxPerFt;
      const xMeters = attrNum(sku, `posX_${roomId}`) ?? row.wM / 2;
      const zMeters = attrNum(sku, `posZ_${roomId}`) ?? row.dM / 2;
      const xPx = px.x + (xMeters * metrics.ftPerM * metrics.pxPerFt) - wPx / 2;
      const yPx = px.y + (zMeters * metrics.ftPerM * metrics.pxPerFt) - dPx / 2;
      editor.createShape({
        id: createShapeId(`sku-${sku.id}`),
        type: "geo",
        x: xPx,
        y: yPx,
        props: {
          geo: "rectangle",
          w: wPx,
          h: dPx,
          color: "orange",
          fill: "semi",
          size: "s",
        },
        meta: { skuLabel: sku.label.split(" · ")[0] ?? sku.label },
      });
    }
    editor.zoomToFit();
  }

  // Silence the asset-record-type unused warning — kept in the import
  // for the next iteration when we attach SKU photos as assets.
  void AssetRecordType;

  useEffect(() => {
    // Future: wire change listeners here to persist SKU position
    // changes back via setPlacementPosition. For the scaffold we run
    // read-only so the existing FloorPlanOverlay remains the source
    // of edits.
  }, [graph, roomId]);

  return (
    <div className="absolute inset-0 w-full h-full" style={{ position: "absolute" }}>
      <Tldraw onMount={setup} hideUi />
    </div>
  );
}
